import React, { useState } from 'react';

import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FunctionsIcon from '@mui/icons-material/Functions';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import TagIcon from '@mui/icons-material/Tag';
import { Box, ButtonBase, IconButton, InputBase, ListItemIcon, Menu, MenuItem, Stack } from '@mui/material';

import type { LensFields } from '~/hooks/apis/reporting/useLensAuthoring';

import type { BoardOperation } from './editor';
import type { BoardEditor } from './useBoardEdit';

export type FieldKind = 'measure' | 'calculated' | 'dimension' | 'date' | 'number';

export type Field = { ref: string; label: string; kind: FieldKind; dataset?: string; column?: string };

const FIELD_TYPE = 'application/x-lens-field';

const GROUPS: Array<[string, Array<FieldKind>]> = [
  ['Dates', ['date']],
  ['Dimensions', ['dimension']],
  ['Measures', ['measure']],
  ['Calculated', ['calculated']],
  ['Numbers to total', ['number']],
];

const ICONS: Record<FieldKind, React.ReactNode> = {
  date: <CalendarMonthOutlinedIcon sx={{ fontSize: 15 }} />,
  dimension: <SellOutlinedIcon sx={{ fontSize: 15 }} />,
  measure: <TagIcon sx={{ fontSize: 15 }} />,
  calculated: <FunctionsIcon sx={{ fontSize: 15 }} />,
  number: <TagIcon sx={{ fontSize: 15 }} />,
};

export const AGGREGATES: Array<[string, string, (column: string) => string]> = [
  ['sum', 'Sum', (column) => `sum(${column})`],
  ['avg', 'Average', (column) => `avg(${column})`],
  ['distinct', 'Count distinct', (column) => `count(distinct ${column})`],
  ['min', 'Lowest', (column) => `min(${column})`],
  ['max', 'Highest', (column) => `max(${column})`],
  ['median', 'Median', (column) => `median(${column})`],
  ['p90', '90th percentile', (column) => `percentile(${column}, 90)`],
];

export function fieldsOf(fields?: LensFields): Array<Field> {
  const datasets = fields?.datasets ?? [];
  const local = fields?.measures ?? [];
  const localIds = new Set(local.map((measure) => measure.id));
  const certified = datasets.flatMap((dataset) => dataset.certified.map((measure) => ({ ...measure, dataset: dataset.id })));
  const shared = (id: string) => certified.filter((measure) => measure.id === id).length > 1;
  const dimensions = new Map<string, Field>();
  for (const dataset of datasets) {
    for (const column of dataset.columns) {
      if (column.kind === 'measure') continue;
      if (!dimensions.has(column.name)) dimensions.set(column.name, { ref: column.name, label: dataset.labels?.[column.name] ?? column.label, kind: column.kind === 'date' ? 'date' : 'dimension', dataset: dataset.id, column: column.name });
    }
    for (const name of Object.keys(dataset.derived ?? {})) if (!dimensions.has(name)) dimensions.set(name, { ref: name, label: dataset.labels?.[name] ?? name, kind: 'dimension', dataset: dataset.id, column: name });
  }
  return [
    ...local.map((measure): Field => ({ ref: measure.id, label: measure.label, kind: measure.expr ? 'calculated' : 'measure', dataset: measure.dataset })),
    ...certified.filter((measure) => !localIds.has(measure.id)).map((measure): Field => ({ ref: shared(measure.id) ? `${measure.dataset}.${measure.id}` : measure.id, label: measure.label, kind: 'measure', dataset: measure.dataset })),
    ...dimensions.values(),
    ...datasets.flatMap((dataset) => dataset.columns.filter((column) => column.kind === 'measure').map((column): Field => ({ ref: `${dataset.id}.${column.name}`, label: dataset.labels?.[column.name] ?? column.label, kind: 'number', dataset: dataset.id, column: column.name }))),
  ];
}

export const isMeasure = (field: Field) => field.kind === 'measure' || field.kind === 'calculated';

export const isDimension = (field: Field) => field.kind === 'dimension' || field.kind === 'date';

export function measureFromNumber(field: Field, aggregate: string, taken: Set<string>): { id: string; operation: BoardOperation } {
  const [, label, formula] = AGGREGATES.find(([id]) => id === aggregate) ?? AGGREGATES[0];
  const base = `${field.column ?? field.ref}_${aggregate}`;
  let id = base;
  for (let count = 2; taken.has(id); count++) id = `${base}_${count}`;
  return { id, operation: { op: 'measure', id, def: { expr: formula(field.ref), from: field.dataset, label: `${label} of ${field.label}` } } };
}

export function startFieldDrag(event: React.DragEvent<HTMLElement>, field: Field) {
  event.dataTransfer.setData(FIELD_TYPE, JSON.stringify(field));
  event.dataTransfer.setData('text/plain', field.ref);
  event.dataTransfer.effectAllowed = 'copy';
}

export function useFieldDrop(onField: (field: Field, target: HTMLElement) => void) {
  const [over, setOver] = useState(false);
  return {
    over,
    props: {
      onDragOver: (event: React.DragEvent<HTMLElement>) => {
        if (!event.dataTransfer.types.includes(FIELD_TYPE)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'copy';
        setOver(true);
      },
      onDragLeave: () => setOver(false),
      onDrop: (event: React.DragEvent<HTMLElement>) => {
        setOver(false);
        const raw = event.dataTransfer.getData(FIELD_TYPE);
        if (!raw) return;
        event.preventDefault();
        onField(JSON.parse(raw) as Field, event.currentTarget);
      },
    },
  };
}

const PINNED_KEY = 'lens-fields:pinned';

function readPinned(): Array<string> {
  try {
    return JSON.parse(window.localStorage.getItem(PINNED_KEY) ?? '[]') as Array<string>;
  } catch {
    return [];
  }
}

type FieldListProps = { fields: Array<Field>; used: Set<string>; onPick: (field: Field, target: HTMLElement) => void; editor: BoardEditor; datasetLabels: (dataset: string) => Record<string, string> | undefined };

export default function FieldList({ fields, used, onPick, editor, datasetLabels }: FieldListProps) {
  const [query, setQuery] = useState('');
  const [pinned, setPinned] = useState(readPinned);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; field: Field }>();
  const [renaming, setRenaming] = useState<{ field: Field; text: string }>();
  const [opened, setOpened] = useState<Set<string>>(() => new Set(['Measures']));
  const wanted = query.trim().toLowerCase();
  const shown = fields.filter((field) => !wanted || field.label.toLowerCase().includes(wanted) || field.ref.toLowerCase().includes(wanted));
  const pin = (field: Field) => {
    const next = pinned.includes(field.ref) ? pinned.filter((ref) => ref !== field.ref) : [...pinned, field.ref];
    setPinned(next);
    try {
      window.localStorage.setItem(PINNED_KEY, JSON.stringify(next));
    } catch {
      return;
    }
  };
  const relabel = (field: Field, text: string) => {
    if (!field.dataset || !field.column) return;
    const labels = Object.fromEntries(Object.entries({ ...datasetLabels(field.dataset), [field.column]: text.trim() }).filter(([, value]) => value));
    editor.apply({ op: 'dataset', id: field.dataset, def: { labels: Object.keys(labels).length ? labels : null } });
  };
  const groups: Array<[string, Array<Field>]> = [
    ['Pinned', shown.filter((field) => pinned.includes(field.ref))],
    ...GROUPS.map(([title, kinds]): [string, Array<Field>] => [title, shown.filter((field) => kinds.includes(field.kind) && !pinned.includes(field.ref))]),
  ];
  return (
    <Stack data-lens-field-list spacing={1}>
      <InputBase
        inputProps={{ 'aria-label': 'Find a field' }}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Find a field"
        startAdornment={<SearchIcon sx={{ color: 'text.secondary', fontSize: 16, mr: 0.75 }} />}
        sx={{ bgcolor: 'action.hover', border: '1px solid transparent', borderRadius: 1, fontSize: 13, minHeight: 28, px: 1 }}
        value={query}
      />
      {groups
        .filter(([, list]) => list.length)
        .map(([title, list]) => (
          <Box key={title}>
            <ButtonBase
              aria-expanded={Boolean(wanted) || title === 'Pinned' || opened.has(title)}
              onClick={() => setOpened(new Set(opened.has(title) ? [...opened].filter((entry) => entry !== title) : [...opened, title]))}
              sx={{ color: 'text.secondary', display: 'flex', fontSize: 11.5, fontWeight: 600, gap: 0.5, justifyContent: 'flex-start', letterSpacing: 0.3, py: 0.5, width: '100%' }}
            >
              {title}
              <Box component="span" sx={{ flex: 1, fontWeight: 400, textAlign: 'right' }}>
                {list.length}
              </Box>
            </ButtonBase>
            {(wanted || title === 'Pinned' || opened.has(title) ? list : []).map((field) =>
              renaming?.field.ref === field.ref ? (
                <InputBase
                  autoFocus
                  inputProps={{ 'aria-label': `Label for ${field.label}` }}
                  key={field.ref}
                  onBlur={() => setRenaming(undefined)}
                  onChange={(event) => setRenaming({ field, text: event.target.value })}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      relabel(field, renaming.text);
                      setRenaming(undefined);
                    }
                    if (event.key === 'Escape') setRenaming(undefined);
                  }}
                  sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 1, fontSize: 13, px: 1, width: '100%' }}
                  value={renaming.text}
                />
              ) : (
                <Box
                  aria-label={`${field.label}, ${field.kind}`}
                  data-lens-field={field.ref}
                  draggable
                  key={field.ref}
                  onClick={(event) => onPick(field, event.currentTarget)}
                  onDragStart={(event) => startFieldDrag(event, field)}
                  onKeyDown={(event) => event.key === 'Enter' && onPick(field, event.currentTarget)}
                  role="button"
                  sx={{
                    alignItems: 'center',
                    borderRadius: 1,
                    color: 'text.primary',
                    cursor: 'grab',
                    display: 'flex',
                    fontSize: 13,
                    gap: 0.75,
                    minHeight: 28,
                    px: 0.75,
                    '&:hover': { bgcolor: 'action.hover' },
                    '&:hover .field-more': { opacity: 1 },
                    '& > svg': { color: field.kind === 'date' || field.kind === 'dimension' ? 'info.main' : 'success.main', flexShrink: 0 },
                  }}
                  tabIndex={0}
                >
                  {ICONS[field.kind]}
                  <Box component="span" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={field.ref}>
                    {field.label}
                  </Box>
                  {used.has(field.ref) ? <Box aria-label="Used on this block" component="span" sx={{ bgcolor: 'primary.main', borderRadius: '50%', flexShrink: 0, height: 5, width: 5 }} /> : null}
                  <IconButton
                    aria-label={`More for ${field.label}`}
                    className="field-more"
                    onClick={(event) => {
                      event.stopPropagation();
                      setMenu({ anchor: event.currentTarget, field });
                    }}
                    size="small"
                    sx={{ opacity: 0, p: 0.25, '&:focus-visible': { opacity: 1 } }}
                  >
                    <MoreHorizIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ),
            )}
          </Box>
        ))}
      <Menu anchorEl={menu?.anchor} onClose={() => setMenu(undefined)} open={Boolean(menu)}>
        <MenuItem
          dense
          onClick={() => {
            if (menu) pin(menu.field);
            setMenu(undefined);
          }}
        >
          <ListItemIcon>{menu && pinned.includes(menu.field.ref) ? <StarIcon fontSize="small" /> : <StarOutlineIcon fontSize="small" />}</ListItemIcon>
          {menu && pinned.includes(menu.field.ref) ? 'Unpin' : 'Pin to the top'}
        </MenuItem>
        {menu?.field.column ? (
          <MenuItem
            dense
            onClick={() => {
              setRenaming({ field: menu.field, text: menu.field.label });
              setMenu(undefined);
            }}
          >
            <ListItemIcon>
              <DriveFileRenameOutlineIcon fontSize="small" />
            </ListItemIcon>
            Relabel
          </MenuItem>
        ) : null}
        <MenuItem
          dense
          onClick={() => {
            if (menu) void navigator.clipboard?.writeText(menu.field.ref).catch(() => undefined);
            setMenu(undefined);
          }}
        >
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          Copy name
        </MenuItem>
      </Menu>
    </Stack>
  );
}
